import {
  createHackathonStage,
  deleteHackathonStage,
  updateHackathonStage,
} from '@/data/services/hackathonStagesServices';
import Hackathon, { HackathonStage } from '@/models/Hackathon';
import alert from '@/utils/shared/alert';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import HackathonStageForm, { HackathonStagesField } from './HackathonStageForm';
import { useTranslation } from 'react-i18next';

type HackathonStagesProps = {
  hackathon: Hackathon;
  hackathonStage: HackathonStage;
  onSuccess?: (stages: HackathonStage) => void;
  onDelete?: (stageId: number) => void;
};

export default function HackathonStageService({
  hackathon,
  onSuccess,
  onDelete: onDeleteExternal,
  hackathonStage,
}: HackathonStagesProps) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'hackathon.form.hackathonStages',
  });
  const [hasChanges, setHasChanges] = useState(false);
  const handleCreate = async (hackathonStage: Partial<HackathonStage>) => {
    const hackathonStagePayload = {
      ...hackathonStage,
      hackathonId: hackathon.id,
    } as HackathonStage;
    const newProject = await createHackathonStage(hackathonStagePayload);
    alert.success(t('successCreatedMessage'));
    return newProject;
  };

  const handleUpdate = async (hackathonStageField: Partial<HackathonStage>) => {
    const hackathonChanges = buildChangedObject(
      hackathonStage,
      hackathonStageField,
    );

    const newProject = await updateHackathonStage(
      hackathonStage.id,
      hackathonChanges,
    );
    alert.success(t('successUpdatedMessage'));

    return newProject;
  };

  const handleSubmit = async (hackathonFields: HackathonStagesField) => {
    const response = !hackathonStage
      ? handleCreate(hackathonFields)
      : handleUpdate(hackathonFields);

    return response;
  };

  const { mutate: onDelete, isLoading: isDeleting } = useMutation(
    deleteHackathonStage,
    {
      onSuccess: (data, variable) => {
        onDeleteExternal?.(variable);
      },
    },
  );

  const { mutate: onSubmit, isLoading } = useMutation(handleSubmit, {
    onError: err => alert.error(getErrorMessage(err)),
    onSuccess: async stages => {
      onSuccess?.(stages);
      setHasChanges(false);
    },
  });
  return (
    <HackathonStageForm
      hackathonStage={hackathonStage}
      onSubmit={onSubmit}
      onDelete={onDelete}
      isLoading={isLoading}
      isDeleting={isDeleting}
      hasChanges={hasChanges}
      setHasChanges={setHasChanges}
    />
  );
}
